import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import type { ReactElement } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { getActionAriaLabelSync } from '@/utils/ariaLabels';

import { AccountBox } from '@/components/AccountBox';
import * as AccountBoxMessages from '@/components/AccountBox/messages';
import { Box } from '@/components/Box';
import { Container } from '@/components/Container';
import { Loader } from '@/components/Loader';
import { getTabId, getTabPanelId, TabList } from '@/components/TabList';
import { FixedPageDocument } from '@/graphql/contentful/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useIsSmDown } from '@/hooks/useMedia';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import {
  type ContentfulFixedPageCMSData,
  generateSectionKey,
  renderContentfulFixedPage,
} from '@/integrations/contentful';
import { SignInLayout } from '@/layouts/SignInLayout';
import {
  DEBOUNCE_TIME_IN_MS,
  GLOBAL_E_MARKET_AVAILABLE,
  REDIRECTION_TYPE,
} from '@/lib/const';
import { EVENT_NAME } from '@/lib/gtm/const';
import { commonMessages } from '@/messages/common';
import { formMessages } from '@/messages/forms';
import { useRegionProvider } from '@/providers/RegionProvider';

import * as M from './messages';
import * as S from './SignInView.styles';

interface SignInViewProps {
  cms?: ContentfulFixedPageCMSData;
}

const TAB = {
  guest: 'guestCheckout',
  signIn: 'signIn',
  signUp: 'signUp',
} as const;
export const SignInView = ({ cms }: SignInViewProps) => {
  const liveCms = useContentfulLiveUpdates(cms, {
    query: FixedPageDocument,
  });
  const { locale, paths, query, replace } = useRouter();
  const redirectionMethod =
    (query?.redirectionMethod as REDIRECTION_TYPE) ?? REDIRECTION_TYPE.AUTO;
  const {
    snapshot: { isAuthenticated, isInitializing: isUserInitializing, user },
  } = useUser();
  const isUserRedirected = useRef(false);
  const { sendEvent } = useGoogleAnalytics();
  const { formatMessage } = useIntl();
  // `locale` above is the router locale string, this one carries the language code
  const { locale: region } = useRegionProvider();
  // tabs exist on mobile only - desktop keeps the two boxes side by side, and
  // must not expose tab/tabpanel roles without a tablist to go with them
  const isMobile = useIsSmDown();

  const [isInitializing, setIsInitializing] = useState<boolean>(
    isAuthenticated === null || isAuthenticated === undefined,
  );

  const isCheckoutVariant = query?.variant === 'checkout';
  // checkout entry defaults to guest checkout, the account page defaults to log in
  const defaultTab = isCheckoutVariant ? TAB.guest : TAB.signIn;
  const [activeTab, setActiveTab] = useState<string>(defaultTab);

  useEffect(() => {
    setIsInitializing(isUserInitializing);
  }, [isUserInitializing]);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  //when reload page, if isAuthenticated not inited, will fetch user,the isUserInitializing will first false ,then true，then false, add a debounced value to void frequent modify
  const debouncedIsInitializing = useDebouncedValue(
    isInitializing,
    DEBOUNCE_TIME_IN_MS,
  );

  useEffect(() => {
    const handleRedirect = async () => {
      if (
        !isUserRedirected.current &&
        user &&
        redirectionMethod === REDIRECTION_TYPE.AUTO
      ) {
        isUserRedirected.current = true;

        sendEvent({
          event: EVENT_NAME.LOGIN,
          account_authentification: 'regular',
          login_type: 'regular',
        });
        await replace((query?.next as string) || paths.myAccount.url());
      }
    };

    handleRedirect();
  }, [user]);

  if (debouncedIsInitializing || user) {
    return <Loader data-test-id='SignInViewLoader' height={400} />;
  }

  const signInBox = (
    <AccountBox>
      <AccountBox.SignIn data-test-id='SignInViewSignIn' />
    </AccountBox>
  );

  // the checkout entry offers guest checkout, the account page offers registration
  const secondaryTabId = isCheckoutVariant ? TAB.guest : TAB.signUp;

  const secondaryBox = isCheckoutVariant ? (
    <AccountBox>
      <AccountBox.GuestCheckout
        data-test-id='SignInViewGuestCheckout-'
        link={{ href: paths.store.checkout.delivery.url() }}
      />
    </AccountBox>
  ) : (
    <AccountBox>
      <AccountBox.SignUp data-test-id='SignInViewSignUp' />
    </AccountBox>
  );

  const signInTab = {
    id: TAB.signIn,
    label: formatMessage(commonMessages.logIn),
  };

  const secondaryTab = {
    id: secondaryTabId,
    label: formatMessage(
      isCheckoutVariant
        ? AccountBoxMessages.messages.checkoutAsGuest
        : formMessages.createMyAccount,
    ),
  };

  // the default tab is rendered first
  const tabs = isCheckoutVariant
    ? [secondaryTab, signInTab]
    : [signInTab, secondaryTab];

  const tabPanelProps = (id: string) =>
    isMobile
      ? {
          'aria-labelledby': getTabId(id),
          id: getTabPanelId(id),
          role: 'tabpanel',
        }
      : {};

  const signInPanel = (
    <S.Panel
      key={TAB.signIn}
      {...tabPanelProps(TAB.signIn)}
      $isActive={activeTab === TAB.signIn}
      sm={6}
    >
      {signInBox}
    </S.Panel>
  );

  const secondaryPanel = (
    <S.Panel
      key={secondaryTabId}
      {...tabPanelProps(secondaryTabId)}
      $isActive={activeTab === secondaryTabId}
      sm={6}
    >
      {secondaryBox}
    </S.Panel>
  );

  // Guest checkout leads on the checkout entry, matching the mobile tab order.
  const panels = isCheckoutVariant
    ? [secondaryPanel, signInPanel]
    : [signInPanel, secondaryPanel];

  return (
    <Box data-test-id='SignInViewBox'>
      {/* The checkout entry keeps its primary CTAs above the fold, so the
          Contentful hero is skipped there. My Account login is unaffected. */}
      {!isCheckoutVariant &&
        liveCms?.topContent?.map((data, i) => (
          <Fragment key={generateSectionKey(data, i)}>
            {renderContentfulFixedPage(
              data,
              undefined,
              liveCms.cmsExtraData,
              true,
              false,
            )}
          </Fragment>
        ))}
      <Container data-test-id='SignInViewContainer'>
        <S.Wrapper
          $hasTopSpacing={isCheckoutVariant}
          data-test-id='SignInViewWrapper'
        >
          {isMobile && (
            <S.TabsWrapper data-test-id='SignInViewTabs'>
              <TabList
                activeItemId={activeTab}
                ariaLabel={
                  getActionAriaLabelSync('account_tabs', region.code) ||
                  'Account options'
                }
                items={tabs}
                onItemClick={setActiveTab}
                variant='segmented'
              />
            </S.TabsWrapper>
          )}
          <S.Row gapDensity='none'>{panels}</S.Row>
        </S.Wrapper>
      </Container>
      {liveCms?.bottomContent?.map((data, i) => (
        <Fragment key={generateSectionKey(data, i)}>
          {renderContentfulFixedPage(
            data,
            undefined,
            liveCms.cmsExtraData,
            true,
            false,
          )}
        </Fragment>
      ))}
      {GLOBAL_E_MARKET_AVAILABLE?.includes(locale) && (
        <Container data-test-id='SignInViewGlobalEContainer'>
          <S.GlobalEMessageWrapper data-test-id='SignInViewGlobalEMessageWrapper'>
            <S.GlobalETitle data-test-id='SignInViewGlobalETitle'>
              {formatMessage(M.messages.globalETitle)}
            </S.GlobalETitle>
            <S.GlobalEDescription data-test-id='SignInViewGlobalEDescription'>
              {formatMessage(M.messages.globalEDescription)}
            </S.GlobalEDescription>
          </S.GlobalEMessageWrapper>
        </Container>
      )}
    </Box>
  );
};

SignInView.getLayout = (page: ReactElement) => (
  <SignInLayout cmsData={page.props.cmsDefaultLayoutData}>{page}</SignInLayout>
);
